import * as acorn from 'acorn';
import { must } from './dom.ts';
import pretty from './pretty-printer.ts';

// NOTES:

// 1. The Shift and Alt key (at least) are both modifiers but also change the
// value of `key` in the Keyboard Event. Therefore some syntactically correct
// bindings such as Alt-d are impossible because the Alt turns the d into ∂.
// Similarly a binding like Shift-a would never happen because only Shift-A can
// occur. (Probably for Shift at least we should just not include it in
// bindings.)

// TODO:

// - History on up and down arrow.
// - Shift movement for selection.
// - Token colorizing.

const OPTS: acorn.Options = { ecmaVersion: 2022 };

type KeyEventLike = { key: string; ctrlKey: boolean; metaKey: boolean; altKey: boolean };

type Binding = (x: KeyEventLike) => void;

const textNode = (s: string) => document.createTextNode(s);

const isExpression = (code: string) => {
  try {
    const p = acorn.parseExpressionAt(code, 0, OPTS);
    if (p.end === code.length) {
      return true;
    }
  } catch {
    // Fallthru to next way
  }

  try {
    const p = acorn.parse(code, OPTS);
    return p.body.length === 1 && p.body[0].type === 'ExpressionStatement';
  } catch {
    return false;
  }
};

const span = (clazz: string, html?: string) => {
  const s = document.createElement('span');
  s.classList.add(clazz);
  if (html !== undefined) {
    s.innerHTML = html;
  }
  return s;
};

class Repl {
  console: Console;
  evaluate: (code: string, source: string) => void;
  evaluating: boolean;
  div: HTMLElement;
  cursor: HTMLSpanElement;
  keybindings: Keybindings;
  current: HTMLDivElement | null;
  history: HTMLDivElement[];
  historyPosition: number;

  constructor(id: string) {
    this.console = new Console((text) => this.log(text));

    this.evaluate = () => {
      throw new Error('Must set repl.evaluate');
    };
    this.evaluating = false;

    this.div = must(document.getElementById(id), `repl element #${id}`);

    this.div.setAttribute('autofocus', true as unknown as string);
    this.div.setAttribute('tabindex', 0 as unknown as string);
    this.cursor = span('cursor', '&nbsp;');
    this.keybindings = new Keybindings();
    this.current = null;
    this.history = [];
    this.historyPosition = 0;

    this.keybindings.bind({
      Backspace: this.backspace,
      Enter: this.enter,
      ArrowLeft: this.left,
      ArrowRight: this.right,
      ArrowUp: this.backInHistory,
      ArrowDown: this.forwardInHistory,
      'Control-a': this.bol,
      'Control-e': this.eol,
      '(': (x) => this.openBracket(x, 'paren'),
      ')': (x) => this.closeBracket(x, 'paren'),
      '[': (x) => this.openBracket(x, 'square'),
      ']': (x) => this.closeBracket(x, 'square'),
      '{': (x) => this.openBracket(x, 'curly'),
      '}': (x) => this.closeBracket(x, 'curly'),
    });

    this.keybindings.bindDefault(this.selfInsert);

    this.div.onkeydown = (e) => {
      // Extract the bits we care about.
      const { key, ctrlKey, metaKey, altKey } = e;
      const x = { key, ctrlKey, metaKey, altKey };
      const b = this.keybindings.getBinding(x);

      if (b) {
        this.clearHighlights();
        b.call(this, x);
        this.maybeHighlightBracket();
        e.stopPropagation();
        e.preventDefault();
      }
    };

    this.div.onpaste = (e) => {
      const data = e.clipboardData?.getData('text/plain') ?? '';
      [...data].forEach((c) => {
        const x = { key: c, ctrlKey: false, metaKey: false, altKey: false };
        const b = this.keybindings.getBinding(x);
        if (b) {
          b.call(this, x);
        }
      });
    };

    this.fixLayout();
    // Similar kludge as in editor.js
    window.addEventListener('resize', () => this.fixLayout());
  }

  fixLayout() {
    // I was defeated in doing this purely through CSS so we just lock the
    // height after the elemnet is laid out.
    this.div.style.height = '100%';
    const rect = this.div.getBoundingClientRect();
    this.div.style.height = `${rect.height}px`;
  }

  restart() {
    this.div.replaceChildren();
    this.newPrompt();
    this.div.focus();
  }

  /*
   * Output a log line in the repl div.
   */
  log(text: string) {
    this.toRepl(text, 'log');
  }

  /*
   * Make the div containing a prompt and the cursor.
   */
  newPrompt() {
    const div = newDivAndPrompt();
    this.addCursor(div);
    this.div.append(div);
    this.current = div;
    this.cursor.scrollIntoView();
  }

  /*
   * Emit a message to the repl.
   */
  message(text: string) {
    this.toRepl(textNode(text), 'message');
  }

  /*
   * Emit an error to the repl.
   */
  error(text: string) {
    this.toRepl(textNode(text), 'error');
    this.newPrompt();
  }

  /*
   * Output a value to the repl.
   */
  print(value: unknown) {
    const span = document.createElement('span');
    const arrow = document.createElement('span');
    arrow.classList.add('output');
    arrow.append(textNode('⇒ '));
    span.append(arrow);
    span.append(textNode(pretty(value)));
    this.toRepl(span, 'value');
    this.newPrompt();
  }

  /*
   * Output to the repl with a particular CSS class.
   */
  toRepl(text: string | Node, clazz: string) {
    const div = document.createElement('div');
    div.classList.add(clazz);
    div.append(text);
    if (clazz === 'log' && !this.evaluating) {
      // console.logs from code in the editor should push the prompt down.
      must(this.current, 'current repl prompt').before(div);
    } else {
      this.div.append(div);
    }
  }

  // The line div containing the cursor. The cursor lives in the current
  // prompt's .text span whenever a prompt exists; fail loudly if not.
  get #line(): HTMLElement {
    return must(this.cursor.parentElement, 'repl input line around cursor');
  }

  addCursor(div: HTMLElement) {
    const eol = must(div.querySelector('.eol'), 'end-of-line marker');
    must(eol.parentElement, 'repl input line').insertBefore(this.cursor, eol);
  }

  clearHighlights() {
    this.#line.querySelectorAll('.highlight').forEach((e) => {
      e.classList.remove('highlight');
      e.classList.remove('wrong-bracket');
    });
  }

  maybeHighlightBracket() {
    const before = this.cursor.previousSibling;
    const after = this.cursor.nextSibling;
    if (isClose(before)) {
      let closed = 0;
      for (let n: ChildNode | null = before; n && !isBol(n); n = n.previousSibling) {
        if (isClose(n)) {
          closed++;
        } else if (isOpen(n)) {
          closed--;
        }
        if (closed === 0) {
          before.classList.add('highlight');
          // The match is always a bracket span (isOpen just matched it).
          if (n instanceof Element) {
            n.classList.add('highlight');
            if (bracketKind(before) !== bracketKind(n)) {
              n.classList.add('wrong-bracket');
            }
          }
          break;
        }
      }
    }
    if (isOpen(after)) {
      let open = 0;
      for (let n: ChildNode | null = after; n && !isEol(n); n = n.nextSibling) {
        if (isOpen(n)) {
          open++;
        } else if (isClose(n)) {
          open--;
        }
        if (open === 0) {
          after.classList.add('highlight');
          // The match is always a bracket span (isClose just matched it).
          if (n instanceof Element) {
            n.classList.add('highlight');
            if (bracketKind(after) !== bracketKind(n)) {
              n.classList.add('wrong-bracket');
            }
          }
          break;
        }
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////
  // Commands

  selfInsert(x: KeyEventLike) {
    this.#line.insertBefore(document.createTextNode(x.key), this.cursor);
  }

  openBracket(x: KeyEventLike, kind: string) {
    const p = span('open', x.key);
    p.classList.add(kind);
    this.#line.insertBefore(p, this.cursor);
  }

  closeBracket(x: KeyEventLike, kind: string) {
    const p = span('close', x.key);
    p.classList.add(kind);
    this.#line.insertBefore(p, this.cursor);
  }

  backspace() {
    const e = this.cursor.previousSibling;
    if (e && !isBol(e)) {
      if (e.nodeType === 3 || e.nodeType === 1) {
        e.parentElement?.removeChild(e);
      }
    }
  }

  enter() {
    const current = must(this.current, 'current repl prompt');
    this.#line.removeChild(this.cursor);
    this.history.push(current);
    this.historyPosition = this.history.length; // after end of history.

    let text = must(current.querySelector<HTMLElement>('.text'), 'repl input text').innerText;

    this.evaluating = true;

    if (isExpression(text.trim())) {
      while (text.endsWith(';')) {
        text = text.substring(0, text.length - 1);
      }
      this.evaluate(`repl.print(\n${text}\n)`, 'repl');
    } else {
      this.evaluate(`\n${text}\nrepl.message("Ok.");`, 'repl');
      this.newPrompt();
    }
    this.evaluating = false;
  }

  left() {
    const e = this.cursor.previousSibling;
    if (e && !isBol(e)) {
      if (e.nodeType === 3 || e.nodeType === 1) {
        e.parentElement?.insertBefore(this.cursor, e);
      }
    }
  }

  right() {
    const e = this.cursor.nextSibling;
    if (e && !isEol(e)) {
      if (e.nodeType === 3 || e.nodeType === 1) {
        e.parentElement?.insertBefore(this.cursor, e.nextSibling);
      }
    }
  }

  backInHistory() {
    if (this.historyPosition > 0) {
      this.goToHistory(this.historyPosition - 1);
    }
  }

  forwardInHistory() {
    if (this.historyPosition < this.history.length - 1) {
      this.goToHistory(this.historyPosition + 1);
    } else if (this.historyPosition < this.history.length) {
      this.historyPosition++;
      this.makeCurrent(newDivAndPrompt());
    }
  }

  goToHistory(pos: number) {
    this.historyPosition = pos;
    this.makeCurrent(this.history[this.historyPosition].cloneNode(true) as HTMLDivElement);
  }

  makeCurrent(div: HTMLDivElement) {
    must(this.current, 'current repl prompt').replaceWith(div);
    this.current = div;
    this.addCursor(this.current);
  }

  bol() {
    const bol = must(this.#line.querySelector('.bol'), 'beginning-of-line marker');
    this.#line.insertBefore(this.cursor, bol.nextSibling);
  }

  eol() {
    const eol = must(this.#line.querySelector('.eol'), 'end-of-line marker');
    this.#line.insertBefore(this.cursor, eol);
  }
}

/*
 * A fake console.
 */
class Console {
  logFn: (text: string) => void;

  constructor(logFn: (text: string) => void) {
    this.logFn = logFn;
  }

  // These actually shouldn't be methods so we can do things like
  // array.forEach(console.log)
  log = (...text: unknown[]) => {
    this.logFn(stringify(text));
  };

  info = (...text: unknown[]) => {
    this.logFn(`INFO: ${stringify(text)}`);
  };

  warn = (...text: unknown[]) => {
    this.logFn(`WARN: ${stringify(text)}`);
  };

  error = (...text: unknown[]) => {
    this.logFn(`ERROR: ${stringify(text)}`);
  };

  debug = (...text: unknown[]) => {
    this.logFn(`DEBUG: ${stringify(text)}`);
  };
}

const stringify = (args: unknown[]) => args.map(String).join(' ');

////////////////////////////////////////////////////////////////////////////////
// Bindings

class Keybindings {
  bindings!: Record<string, Binding>;
  defaultBinding!: Binding;

  static descriptor(x: KeyEventLike) {
    const keys = [];
    // Note: Alt and Meta are likely different on different OSes.
    // If we actually use bindings for either of those may need to
    // provide an option to flip their meaning.
    if (x.ctrlKey) {
      keys.push('Control');
    }
    if (x.altKey) {
      keys.push('Alt');
    }
    if (x.metaKey) {
      keys.push('Meta');
    }
    if (keys.indexOf(x.key) === -1) {
      keys.push(x.key);
    }
    return keys.join('-');
  }

  bind(bindings: Record<string, Binding>) {
    this.bindings = bindings;
  }

  bindDefault(defaultBinding: Binding) {
    this.defaultBinding = defaultBinding;
  }

  getBinding(e: KeyEventLike) {
    const descriptor = Keybindings.descriptor(e);

    if (descriptor in this.bindings) {
      // console.log(`${descriptor} is bound`);
      return this.bindings[descriptor];
    }
    if (!(e.ctrlKey || e.altKey || e.metaKey) && [...e.key].length === 1) {
      // console.log(`Using default binding for ${descriptor}`);
      return this.defaultBinding;
    }
    // console.log(`No binding for ${descriptor}`);
    return false;
  }
}

const hasClass = (n: Node | null, clazz: string): boolean =>
  n instanceof Element && n.classList.contains(clazz);

const isBol = (n: Node | null) => hasClass(n, 'bol');

const isEol = (n: Node | null) => hasClass(n, 'eol');

const isClose = (n: Node | null): n is Element => hasClass(n, 'close');

const isOpen = (n: Node | null): n is Element => hasClass(n, 'open');

const bracketKind = (n: Node | null) => ['paren', 'square', 'curly'].find((k) => hasClass(n, k));

const newDivAndPrompt = () => {
  const div = document.createElement('div');
  div.append(span('prompt', '»'));

  const text = span('text');
  text.append(span('bol'));
  text.append(span('eol'));
  div.append(text);
  return div;
};

const replize = (id: string) => new Repl(id);

export type { Repl };

export default replize;
